class InvalidDateAdapterError extends Error {}
let dateAdapterConfig;
class DateAdapterBase {
  constructor(_date, options) {
    /**
     * The metadata property is intended to store arbitrary data
     * related to this DateAdapter. Unlike other properties, the
     * metadata property is intended to be mutable.
     */
    this.metadata = {};
    this.duration = (options && options.duration) || 0;
    this.generators = (options && options.generators && options.generators.slice()) || [];
    if (!Number.isInteger(this.duration) || this.duration < 0) {
      throw new InvalidDateAdapterError('duration must be a non-negative integer');
    }
  }
  static set adapter(value) {
    if (dateAdapterConfig) {
      throw new Error(`"${dateAdapterConfig.name}" has already been configured.`);
    }
    dateAdapterConfig = value;
  }
  static get adapter() {
    if (!dateAdapterConfig) {
      throw new Error('No date adapter has been configured. See rSchedule docs.');
    }
    return dateAdapterConfig;
  }
  static isDate(_object) {
    throw unimplementedError('isDate()');
  }
  static fromDate(_date, _options) {
    throw unimplementedError('fromDate()');
  }
  static fromJSON(_json) {
    throw unimplementedError('fromJSON()');
  }
  static fromDateTime(_datetime) {
    throw unimplementedError('fromDateTime()');
  }
  /**
   * Returns a string in simplified extended ISO format (ISO 8601).
   *
   * _Note: this method is intended for testing and its
   * implementation isn't particularly performant._
   */
  toISOString() {
    return this.set('timezone', 'UTC')
      .toDateTime()
      .toISOString();
  }
  toDateTime() {
    const date = DateTime.fromJSON(
      Object.assign(Object.assign({}, this.toJSON()), {
        generators: this.generators,
        metadata: this.metadata,
      }),
    );
    return date;
  }
}
DateAdapterBase.hasTimezoneSupport = false;
function unimplementedError(name) {
  return new Error(`You must implement the "${name}" method for this DateAdapter class`);
}
var DateAdapter;
(function(DateAdapter) {
  DateAdapter.WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  DateAdapter.MILLISECONDS_IN_SECOND = 1000;
  DateAdapter.MILLISECONDS_IN_MINUTE = DateAdapter.MILLISECONDS_IN_SECOND * 60;
  DateAdapter.MILLISECONDS_IN_HOUR = DateAdapter.MILLISECONDS_IN_MINUTE * 60;
  DateAdapter.MILLISECONDS_IN_DAY = DateAdapter.MILLISECONDS_IN_HOUR * 24;
  DateAdapter.MILLISECONDS_IN_WEEK = DateAdapter.MILLISECONDS_IN_DAY * 7;
})(DateAdapter || (DateAdapter = {}));
class InvalidDateTimeError extends Error {}
class DateTime {
  constructor(date, timezone, duration, generators, options = {}) {
    this.metadata = {};
    this.date = new Date(date);
    this.timezone = timezone || null;
    this.duration = duration || 0;
    this.generators = (generators && generators.slice()) || [];
    if (!Number.isInteger(this.duration) || this.duration < 0) {
      throw new InvalidDateTimeError('duration must be a non-negative integer');
    }
    if (options.metadata) {
      Object.assign(this.metadata, options.metadata);
    }
    this.assertIsValid();
  }
  // /**
  //  * Similar to `Array.isArray()`, `isInstance()` provides a surefire method
  //  * of determining if an object is a `DateTime` by checking against the
  //  * global symbol registry.
  //  */
  // static isInstance(object: any): object is DateTime {
  //   return !!(object && object[DATETIME_ID]);
  // }
  static fromJSON(json) {
    const date = new Date(
      Date.UTC(
        json.year,
        json.month - 1,
        json.day,
        json.hour,
        json.minute,
        json.second,
        json.millisecond,
      ),
    );
    return new DateTime(date, json.timezone, json.duration, json.generators, {
      metadata: json.metadata,
    });
  }
  static fromDateAdapter(adapter) {
    return DateTime.fromJSON(
      Object.assign(Object.assign({}, adapter.toJSON()), {
        generators: adapter.generators,
        metadata: adapter.metadata,
      }),
    );
  }
  /**
   * Returns `undefined` if `duration` is `0`. Else returns
   * the `end` date.
   */
  get end() {
    if (!this.duration) return;
    if (this._end) return this._end;
    this._end = this.add(this.duration, 'millisecond');
    return this._end;
  }
  // While we constrain the argument to be another DateAdapter in typescript
  // we handle the case of someone passing in another type of object in javascript
  isEqual(object) {
    if (!object) {
      return false;
    }
    assertSameTimeZone(this, object);
    return this.valueOf() === object.valueOf();
  }
  isBefore(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() < object.valueOf();
  }
  isBeforeOrEqual(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() <= object.valueOf();
  }
  isAfter(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() > object.valueOf();
  }
  isAfterOrEqual(object) {
    assertSameTimeZone(this, object);
    return this.valueOf() >= object.valueOf();
  }
  isOccurring(object) {
    if (!this.duration) {
      throw new Error('DateTime#isOccurring() is only applicable to DateTimes with durations');
    }
    assertSameTimeZone(this, object);
    return (
      object.isAfterOrEqual(this) && object.isBeforeOrEqual(this.add(this.duration, 'millisecond'))
    );
  }
  add(amount, unit) {
    switch (unit) {
      case 'generator': {
        const generators = this.generators.slice();
        generators.unshift(amount);
        return new DateTime(this.date, this.timezone, this.duration, generators);
      }
      case 'year':
        return this.forkDateTime(addUTCYears(this.date, amount));
      case 'month':
        return this.forkDateTime(addUTCMonths(this.date, amount));
      case 'week':
        return this.forkDateTime(addUTCWeeks(this.date, amount));
      case 'day':
        return this.forkDateTime(addUTCDays(this.date, amount));
      case 'hour':
        return this.forkDateTime(addUTCHours(this.date, amount));
      case 'minute':
        return this.forkDateTime(addUTCMinutes(this.date, amount));
      case 'second':
        return this.forkDateTime(addUTCSeconds(this.date, amount));
      case 'millisecond':
        return this.forkDateTime(addUTCMilliseconds(this.date, amount));
      default:
        throw new Error('Invalid unit provided to `DateTime#add`');
    }
  }
  subtract(amount, unit) {
    switch (unit) {
      case 'year':
        return this.forkDateTime(subUTCYears(this.date, amount));
      case 'month':
        return this.forkDateTime(subUTCMonths(this.date, amount));
      case 'week':
        return this.forkDateTime(subUTCWeeks(this.date, amount));
      case 'day':
        return this.forkDateTime(subUTCDays(this.date, amount));
      case 'hour':
        return this.forkDateTime(subUTCHours(this.date, amount));
      case 'minute':
        return this.forkDateTime(subUTCMinutes(this.date, amount));
      case 'second':
        return this.forkDateTime(subUTCSeconds(this.date, amount));
      case 'millisecond':
        return this.forkDateTime(subUTCMilliseconds(this.date, amount));
      default:
        throw new Error('Invalid unit provided to `DateTime#subtract`');
    }
  }
  get(unit) {
    switch (unit) {
      case 'year':
        return this.date.getUTCFullYear();
      case 'month':
        return this.date.getUTCMonth() + 1;
      case 'yearday':
        return getUTCYearDay(this.date);
      case 'weekday':
        return DateAdapter.WEEKDAYS[this.date.getUTCDay()];
      case 'day':
        return this.date.getUTCDate();
      case 'hour':
        return this.date.getUTCHours();
      case 'minute':
        return this.date.getUTCMinutes();
      case 'second':
        return this.date.getUTCSeconds();
      case 'millisecond':
        return this.date.getUTCMilliseconds();
      default:
        throw new Error('Invalid unit provided to `DateTime#set`');
    }
  }
  set(unit, value) {
    if (unit === 'duration') {
      return new DateTime(this.date, this.timezone, value, this.generators);
    }
    if (unit === 'generators') {
      return new DateTime(this.date, this.timezone, this.duration, value);
    }
    let date = new Date(this.date);
    switch (unit) {
      case 'year':
        date.setUTCFullYear(value);
        break;
      case 'month': {
        // If the current day of the month
        // is greater than days in the month we are moving to, we need to also
        // set the day to the end of that month.
        const length = monthLength(value, date.getUTCFullYear());
        const day = date.getUTCDate();
        if (day > length) {
          date.setUTCDate(1);
          date.setUTCMonth(value);
          date = subUTCDays(date, 1);
        } else {
          date.setUTCMonth(value - 1);
        }
        break;
      }
      case 'day':
        date.setUTCDate(value);
        break;
      case 'hour':
        date.setUTCHours(value);
        break;
      case 'minute':
        date.setUTCMinutes(value);
        break;
      case 'second':
        date.setUTCSeconds(value);
        break;
      case 'millisecond':
        date.setUTCMilliseconds(value);
        break;
      default:
        throw new Error('Invalid unit provided to `DateTime#set`');
    }
    return this.forkDateTime(date);
  }
  granularity(granularity, opt = {}) {
    let date = this.forkDateTime(this.date);
    switch (granularity) {
      case 'year':
        date = date.set('month', 1);
      case 'month':
        date = date.set('day', 1);
        break;
      case 'week':
        date = setDateToStartOfWeek(date, opt.weekStart);
    }
    switch (granularity) {
      case 'year':
      case 'month':
      case 'week':
      case 'day':
        date = date.set('hour', 0);
      case 'hour':
        date = date.set('minute', 0);
      case 'minute':
        date = date.set('second', 0);
      case 'second':
        date = date.set('millisecond', 0);
      case 'millisecond':
        return date;
      default:
        throw new Error('Invalid granularity provided to `DateTime#granularity`: ' + granularity);
    }
  }
  endGranularity(granularity, opt = {}) {
    let date = this.forkDateTime(this.date);
    switch (granularity) {
      case 'year':
        date = date.set('month', 12);
      case 'month':
        date = date.set('day', monthLength(date.get('month'), date.get('year')));
        break;
      case 'week':
        date = setDateToEndOfWeek(date, opt.weekStart);
    }
    switch (granularity) {
      case 'year':
      case 'month':
      case 'week':
      case 'day':
        date = date.set('hour', 23);
      case 'hour':
        date = date.set('minute', 59);
      case 'minute':
        date = date.set('second', 59);
      case 'second':
        date = date.set('millisecond', 999);
      case 'millisecond':
        return date;
      default:
        throw new Error('Invalid granularity provided to `DateTime#granularity`: ' + granularity);
    }
  }
  toISOString() {
    return this.date.toISOString();
  }
  toDateTime() {
    return this;
  }
  toJSON() {
    const json = {
      timezone: this.timezone,
      year: this.get('year'),
      month: this.get('month'),
      day: this.get('day'),
      hour: this.get('hour'),
      minute: this.get('minute'),
      second: this.get('second'),
      millisecond: this.get('millisecond'),
    };
    if (this.duration) {
      json.duration = this.duration;
    }
    return json;
  }
  valueOf() {
    return this.date.valueOf();
  }
  assertIsValid() {
    if (isNaN(this.valueOf())) {
      throw new InvalidDateTimeError('DateTime has invalid date.');
    }
    return true;
  }
  forkDateTime(date) {
    return new DateTime(date, this.timezone, this.duration, this.generators);
  }
}
function assertSameTimeZone(x, y) {
  if (x.timezone !== y.timezone) {
    throw new InvalidDateTimeError(
      'Attempted to compare a datetime to another date in a different timezone: ' +
        JSON.stringify(x) +
        ' and ' +
        JSON.stringify(y),
    );
  }
  return true;
}
function setDateToStartOfWeek(date, wkst) {
  const index = orderedWeekdays(wkst).indexOf(date.get('weekday'));
  return date.subtract(index, 'day');
}
function setDateToEndOfWeek(date, wkst) {
  const index = orderedWeekdays(wkst).indexOf(date.get('weekday'));
  return date.add(6 - index, 'day');
}
function dateTimeSortComparer(a, b) {
  if (a.isAfter(b)) return 1;
  if (a.isBefore(b)) return -1;
  if (a.duration && b.duration) {
    if (a.duration > b.duration) return 1;
    if (a.duration < b.duration) return -1;
  }
  return 0;
}
function uniqDateTimes(dates) {
  return Array.from(new Map(dates.map(date => [date.toISOString(), date])).values());
}
function orderedWeekdays(wkst = 'SU') {
  const wkdays = DateAdapter.WEEKDAYS.slice();
  let index = wkdays.indexOf(wkst);
  while (index !== 0) {
    shiftArray(wkdays);
    index--;
  }
  return wkdays;
}
function shiftArray(array, from = 'first') {
  if (array.length === 0) {
    return array;
  } else if (from === 'first') {
    array.push(array.shift());
  } else {
    array.unshift(array.pop());
  }
  return array;
}
function getDifferenceBetweenWeekdays(x, y) {
  if (x === y) return 0;
  const result = DateAdapter.WEEKDAYS.indexOf(x) - DateAdapter.WEEKDAYS.indexOf(y);
  return result > 0 ? 7 - result : Math.abs(result);
}
/**
 * Returns the days in the given month.
 *
 * @param month base-1
 * @param year
 */
function monthLength(month, year) {
  const block = {
    1: 31,
    2: getDaysInFebruary(year),
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };
  return block[month];
}
function getDaysInFebruary(year) {
  return isLeapYear(year) ? 29 : 28;
}
// taken from date-fn
function isLeapYear(year) {
  return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
}
function getDaysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}
function getUTCYearDay(now) {
  const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  const diff = now.valueOf() - start.valueOf();
  return 1 + Math.floor(diff / DateAdapter.MILLISECONDS_IN_DAY);
}
/**
 * These functions are basically lifted from `date-fns`, but changed
 * to use the UTC date methods, which `date-fns` doesn't support.
 */
function toInteger(input) {
  if (input === null || input === true || input === false) {
    return NaN;
  }
  const int = Number(input);
  if (isNaN(int)) {
    return int;
  }
  return int < 0 ? Math.ceil(int) : Math.floor(int);
}
function addMilliseconds(dirtyDate, dirtyAmount) {
  if (arguments.length < 2) {
    throw new TypeError('2 arguments required, but only ' + arguments.length + ' present');
  }
  const timestamp = dirtyDate.valueOf();
  const amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}
function addUTCYears(date, input) {
  const amount = toInteger(input);
  return addUTCMonths(date, amount * 12);
}
function addUTCMonths(date, input) {
  const amount = toInteger(input);
  date = new Date(date);
  const desiredMonth = date.getUTCMonth() + amount;
  const dateWithDesiredMonth = new Date(0);
  dateWithDesiredMonth.setUTCFullYear(date.getUTCFullYear(), desiredMonth, 1);
  dateWithDesiredMonth.setUTCHours(0, 0, 0, 0);
  const daysInMonth = monthLength(
    dateWithDesiredMonth.getUTCMonth() + 1,
    dateWithDesiredMonth.getUTCFullYear(),
  );
  // Set the last day of the new month
  // if the original date was the last day of the longer month
  date.setUTCMonth(desiredMonth, Math.min(daysInMonth, date.getUTCDate()));
  return date;
}
function addUTCWeeks(date, input) {
  const amount = toInteger(input);
  const days = amount * 7;
  return addUTCDays(date, days);
}
function addUTCDays(date, input) {
  // by adding milliseconds rather than days, we supress the native Date object's automatic
  // daylight savings time conversions which we don't want in UTC mode
  return addUTCMilliseconds(date, toInteger(input) * DateAdapter.MILLISECONDS_IN_DAY);
}
function addUTCHours(date, input) {
  const amount = toInteger(input);
  return addMilliseconds(date, amount * DateAdapter.MILLISECONDS_IN_HOUR);
}
function addUTCMinutes(date, input) {
  const amount = toInteger(input);
  return addMilliseconds(date, amount * DateAdapter.MILLISECONDS_IN_MINUTE);
}
function addUTCSeconds(date, input) {
  const amount = toInteger(input);
  return addMilliseconds(date, amount * DateAdapter.MILLISECONDS_IN_SECOND);
}
function addUTCMilliseconds(date, input) {
  const amount = toInteger(input);
  const timestamp = date.getTime();
  return new Date(timestamp + amount);
}
function subUTCYears(date, amount) {
  return addUTCYears(date, -amount);
}
function subUTCMonths(date, amount) {
  return addUTCMonths(date, -amount);
}
function subUTCWeeks(date, amount) {
  return addUTCWeeks(date, -amount);
}
function subUTCDays(date, amount) {
  return addUTCDays(date, -amount);
}
function subUTCHours(date, amount) {
  return addUTCHours(date, -amount);
}
function subUTCMinutes(date, amount) {
  return addUTCMinutes(date, -amount);
}
function subUTCSeconds(date, amount) {
  return addUTCSeconds(date, -amount);
}
function subUTCMilliseconds(date, amount) {
  return addUTCMilliseconds(date, -amount);
}

class ArgumentError extends Error {}
class InfiniteLoopError extends Error {}
function numberSortComparer(a, b) {
  if (a > b) {
    return 1;
  } else if (b > a) {
    return -1;
  } else {
    return 0;
  }
}
function freqToGranularity(freq) {
  switch (freq) {
    case 'YEARLY':
      return 'year';
    case 'MONTHLY':
      return 'month';
    case 'WEEKLY':
      return 'week';
    case 'DAILY':
      return 'day';
    case 'HOURLY':
      return 'hour';
    case 'MINUTELY':
      return 'minute';
    case 'SECONDLY':
      return 'second';
    case 'MILLISECONDLY':
      return 'millisecond';
    default:
      throw new Error('unknown freq passed to freqToGranularity()');
  }
}
function cloneJSON(json) {
  return JSON.parse(JSON.stringify(json));
}
function dateInputToDateAdapter(date) {
  // prettier-ignore
  return date instanceof DateTime ? DateAdapterBase.adapter.fromDateTime(date) :
        date instanceof DateAdapterBase ? date :
            DateAdapterBase.adapter.fromDate(date);
}
function dateInputToDateTime(date, timezone) {
  if (date instanceof DateTime) {
    if (date.timezone !== timezone) {
      return DateAdapterBase.adapter
        .fromDateTime(date)
        .set('timezone', timezone)
        .toDateTime();
    }
    return date;
  }
  return date instanceof DateAdapterBase
    ? date.set('timezone', timezone).toDateTime()
    : DateAdapterBase.adapter
        .fromDate(date)
        .set('timezone', timezone)
        .toDateTime();
}
// export function normalizeDateInput<T extends DateAdapter>(
//   dateAdapter: DateAdapterConstructor<T>,
//   input: DateInput<T>,
//   timezone: string | null,
// ): DateTime {
//   if (input instanceof DateTime) {
//     if (input.timezone !== timezone) {
//       return dateAdapter
//         .fromDateTime(input)
//         .set('timezone', timezone)
//         .toDateTime();
//     }
//     return input;
//   }
//   return input instanceof DateAdapter
//     ? input.set('timezone', timezone).toDateTime()
//     : new dateAdapter(input).toDateTime();
// }
function normalizeDateTimeTimezone(date, timezone) {
  if (date.timezone !== timezone) {
    return DateAdapterBase.adapter
      .fromDateTime(date)
      .set('timezone', timezone)
      .toDateTime();
  }
  return date;
}

class RuleOptionError extends Error {}
function normalizeDefaultOptions(options) {
  let start;
  if (options.start instanceof DateTime) {
    start = options.start;
  } else if (options.start instanceof DateAdapterBase) {
    start = options.start.toDateTime();
  } else if (DateAdapterBase.adapter.isDate(options.start)) {
    start = DateAdapterBase.adapter.fromDate(options.start).toDateTime();
  } else {
    throw new RuleOptionError(
      '"start" must be either a `DateAdapter` instance or an instance of the ' +
        'date a DateAdapter is wrapping (e.g. `StandardDateAdapter` wraps a `Date`)',
    );
  }
  let end;
  if (options.end) {
    if (options.end instanceof DateTime) {
      end = options.end;
    } else if (options.end instanceof DateAdapterBase) {
      end = options.end.toDateTime();
    } else if (DateAdapterBase.adapter.isDate(options.end)) {
      end = DateAdapterBase.adapter.fromDate(options.end).toDateTime();
    } else {
      throw new RuleOptionError(
        '"end" must be either be `undefined`, a `DateAdapter` instance, or an instance of the ' +
          'date a DateAdapter is wrapping (e.g. `StandardDateAdapter` wraps a `Date`)',
      );
    }
  }
  if (options.duration !== undefined) {
    if (!Number.isInteger(options.duration)) {
      throw new RuleOptionError('"duration" expects a whole number');
    }
    if (options.duration <= 0) {
      throw new RuleOptionError('"duration" must be greater than 0');
    }
  }
  if (options.count !== undefined) {
    if (!Number.isInteger(options.count)) {
      throw new RuleOptionError('"count" must be a whole number');
    }
    if (options.count < 0) {
      throw new RuleOptionError('"count" must be greater than 0');
    }
  }
  if (options.end !== undefined && options.count !== undefined) {
    throw new RuleOptionError('"end" and "count" cannot both be present');
  }
  return {
    start,
    end,
    count: options.count,
    duration: options.duration,
  };
}
function normalizeRuleOptions(recurrenceModules, options) {
  const normOptions = normalizeDefaultOptions(options);
  const startOptions = cloneJSON(options);
  recurrenceModules.forEach(mod => {
    mod.normalizeOptions(startOptions, normOptions);
  });
  Object.keys(normOptions).forEach(key => {
    if (normOptions[key] === undefined) {
      delete normOptions[key];
    }
  });
  return normOptions;
}
function cloneRuleOptions(options) {
  const obj = cloneJSON(options);
  obj.start = options.start;
  if (options.end) obj.end = options.end;
  return obj;
}

class RecurrenceRuleError extends Error {}
class ValidDateTime {
  constructor(date) {
    this.date = date;
  }
}
class InvalidDateTime {
  constructor(date) {
    this.date = date;
  }
}
function recurrenceRulesReducer(rules) {
  return iterator =>
    rules.reduce((prev, curr) => {
      const rule = curr.get(iterator);
      if (rule) prev.push(rule);
      return prev;
    }, []);
}

class RecurrenceRulesIterator {
  constructor(recurrenceRules, options, args) {
    this.options = options;
    this.args = args;
    this.rules = [];
    this.iterator =
      // prettier-ignore
      this.options.count === undefined ? this.iterate() :
            this.args.reverse ? this.iterateWithReverseCount() :
                this.iterateWithCount();
    this.options = Object.assign(Object.assign({}, cloneJSON(options)), {
      start: options.start,
      end: options.end,
    });
    this.reverse = (this.options.count === undefined && args.reverse) || false;
    const { start, end } = this.normalizeDateTimeArgs(args);
    if (options.count !== undefined) {
      this.start = options.start;
    } else if (start && options.start) {
      this.start = start.isAfterOrEqual(options.start) ? start : options.start;
    } else {
      this.start = start || options.start;
    }
    if (end && options.end) {
      this.end = end.isBeforeOrEqual(options.end) ? end : options.end;
    } else {
      this.end = end || options.end;
    }
    if (this.args.reverse && !(options.count !== undefined || this.end)) {
      throw new Error(
        'When iterating in reverse, the rule must have an `end` or `count` ' +
          'property or you must provide an `end` argument.',
      );
    }
    this.isInfinite = !this.end && this.options.count === undefined;
    this.hasDuration = !!this.options.duration;
    this.rules = Array.isArray(recurrenceRules) ? recurrenceRules : recurrenceRules(this);
  }
  [Symbol.iterator]() {
    return this.iterator;
  }
  next(args) {
    return this.iterator.next(args);
  }
  /**
   * In the pipe controller, we have an extra level of indirection with
   * the `run()` and `iterate()` methods. The `iterate()` method is the
   * method which actually runs the logic in the pipes. If we didn't
   * need to account for the `count` property of a rule, we would *only*
   * need the iterate method... so much simpler. But we do need to account
   * for rules with a `count` property.
   *
   * Rules with a `count` property need to begin iteration at the beginning
   * because the `count` is always from the rule's start time. So if someone
   * passes in a new start time as an argument to a rule with `count`, we
   * need to secretly iterate from the beginning, tracking the number of
   * iterations, and then only start yielding dates when we reach the section
   * the user cares about (or, if we hit our `count` quota, cancel iterating).
   *
   * Additionally, we need to handle iterating in reverse. In this case, we build
   * up a cache of dates between the rule's start time and the reverse iteration
   * start date. Once we hit the reverse iteration start date, we start
   * yielding dates in the cache, in reverse order.
   *
   * In general, I imagine the count number, if used, will be small. But a large
   * count will definitely have a negative performance affect. I don't think
   * there's anything to be done about this.
   */
  *iterateWithReverseCount() {
    const dates = Array.from(this.iterateWithCount()).reverse();
    let yieldArgs;
    const dateCache = dates.slice();
    let date = dateCache.shift();
    while (date) {
      if (yieldArgs && yieldArgs.skipToDate && date.isAfter(yieldArgs.skipToDate)) {
        date = dateCache.shift();
        continue;
      }
      yieldArgs = yield date;
      if (yieldArgs && yieldArgs.skipToDate && yieldArgs.skipToDate.isAfterOrEqual(date)) {
        throw new Error(
          'A provided `skipToDate` option must be greater than the last yielded date ' +
            '(or smaller, in the case of reverse iteration)',
        );
      }
      date = dateCache.shift();
    }
    return undefined;
  }
  *iterateWithCount() {
    if (this.options.count === 0) return;
    const iterable = this.iterate();
    const start = this.args.start || this.start;
    let date = iterable.next().value;
    let index = 1;
    let yieldArgs;
    while (date && index <= this.options.count) {
      index++;
      if (date.isBefore(start)) {
        date = iterable.next().value;
        continue;
      }
      if (yieldArgs && yieldArgs.skipToDate && date.isBefore(yieldArgs.skipToDate)) {
        date = iterable.next().value;
        continue;
      }
      yieldArgs = yield date;
      if (yieldArgs && yieldArgs.skipToDate && yieldArgs.skipToDate.isBeforeOrEqual(date)) {
        throw new Error(
          'A provided `skipToDate` option must be greater than the last yielded date ' +
            '(or smaller, in the case of reverse iteration)',
        );
      }
      date = iterable.next().value;
    }
    return undefined;
  }
  *iterate() {
    let startingDate = this.start;
    if (this.reverse) startingDate = this.end;
    let date = this.nextDate(startingDate);
    while (date) {
      const args = yield this.normalizeRunOutput(date);
      if (args && args.skipToDate) {
        if (
          this.reverse
            ? args.skipToDate.isAfterOrEqual(date)
            : args.skipToDate.isBeforeOrEqual(date)
        ) {
          // We cannot consistently skip backwards because after an iterator is "done"
          // it always returns undefined and you cannot reset it. Theoretically, it would be
          // fine to skip backwards if the iterator wasn't already "done", but this
          // would be prone to user error so we simply disallow skipping backwards altogether.
          throw new Error(
            'A provided `skipToDate` option must be greater than the last yielded date ' +
              '(or smaller, in the case of reverse iteration)',
          );
        }
        date = this.nextDate(args.skipToDate);
      } else {
        date = this.nextDate(
          this.reverse ? date.subtract(1, 'millisecond') : date.add(1, 'millisecond'),
        );
      }
    }
    return undefined;
  }
  /**
   * Loops through the recurrence rules until a valid date is found.
   */
  nextDate(start) {
    let result = this.runRules(start.set('generators', []));
    if (this.isDatePastEnd(result.date)) return null;
    let index = 0;
    while (result instanceof InvalidDateTime && index < 50) {
      result = this.runRules(result.date);
      if (this.isDatePastEnd(result.date)) return null;
      index++;
    }
    if (result instanceof InvalidDateTime) {
      throw new RecurrenceRuleError(
        `Failed to find a matching occurrence in ${index} iterations. ` +
          `Last iterated date: "${result.date.toISOString()}"`,
      );
    }
    if (this.reverse ? start.isBefore(result.date) : start.isAfter(result.date)) {
      throw new RecurrenceRuleError(
        'An error occurred in a recurrence rule. If this happened using ' +
          'the rSchedule provided recurrence rules, you should ' +
          'open an issue in the rSchedule repo. The maintainer is going to ' +
          'want to know how to recreate the error.',
      );
    }
    return result.date;
  }
  /**
   * Performs one run of the recurrence rules and returns the result.
   * It's a slightly optimized reducer function.
   */
  runRules(start) {
    let result = new ValidDateTime(start);
    for (const rule of this.rules) {
      if (result instanceof InvalidDateTime) {
        return result;
      }
      result = rule.run(result.date);
    }
    return result;
  }
  isDatePastEnd(date) {
    return this.reverse ? date.isBefore(this.start) : this.end && date.isAfter(this.end);
  }
  normalizeRunOutput(date) {
    return this.hasDuration ? date.set('duration', this.options.duration) : date;
  }
  normalizeDateTimeArgs(args) {
    return {
      start: args.start && normalizeDateTimeTimezone(args.start, this.options.start.timezone),
      end: args.end && normalizeDateTimeTimezone(args.end, this.options.start.timezone),
    };
  }
}

export {
  ArgumentError,
  DateAdapter,
  DateAdapterBase,
  DateTime,
  InfiniteLoopError,
  InvalidDateAdapterError,
  InvalidDateTime,
  InvalidDateTimeError,
  RecurrenceRuleError,
  RecurrenceRulesIterator,
  RuleOptionError,
  ValidDateTime,
  cloneJSON,
  cloneRuleOptions,
  dateInputToDateAdapter,
  dateInputToDateTime,
  dateTimeSortComparer,
  freqToGranularity,
  getDaysInYear,
  getDifferenceBetweenWeekdays,
  isLeapYear,
  normalizeDateTimeTimezone,
  normalizeRuleOptions,
  numberSortComparer,
  orderedWeekdays,
  recurrenceRulesReducer,
  uniqDateTimes,
};
